import { createRoot, Root } from "react-dom/client";
import { SimilarDrawer } from "../sheets";
import { Product } from "@/sdk/types.ts";
import InlineBlock from "./components/inline-block";
import RecentlyViewed from "./components/recently-viewed";
import { HeliumDrawer } from "./components/btn-drawer";
import Filters from "./components/filters";
import CartGoals from "./components/cart-goals";
import { cloneNode } from "./ui-script/utils";
import CartBlock from "./components/cart-block";
import { FAQ, FaqPillet } from "./components/faq-pillet";
import { Recommendation } from "./components/recommendation";
import { handleEvent } from "./components/track/util";
import MemoryRecomendationPillets from "./components/memory-recommendation-pillets";
import { ActiveMemory } from "@/memory/types";
import { createElementFromString } from "@/datastore/utils";
import { crossSellVariable } from "@/brand-specific/cc/cross-sell";
import ComplementaryStylesRecommendation from "./components/complementary-styles";
import { Grid2x2Check, Shirt } from "lucide-react";
import RecommendationWithoutDrawer from "./components/recommendation-without-drawer";
import FestivalCollection from "./components/festival-collections/festival-collections";
import { addViewListener, ComponentTypes } from "@/utils/tracking";
import CollectionFilters from "./components/collection-filters";
import FloatingChat from "./components/floating-chat/floating-chat";
import PriceLevelColllection from "./components/price-level-collection";
interface ElementWithReactRoot extends HTMLElement {
  _reactRootContainer?: {
    _internalRoot: Root;
  };
}

export class UISDK {
  root: Root;
  constructor() {
    const ele = document.createElement("div");
    ele.id = "helium-sheet-root";
    // document.body.appendChild(ele);
    // const stylesheet = document.createElement("link");
    // stylesheet.href =
    //   "https://ekagnpcuovttf3s3.public.blob.vercel-storage.com/SDK/ui.css";
    // stylesheet.rel = "stylesheet";
    // document.head.appendChild(stylesheet);

    this.root = createRoot(ele);
  }

  renderSimilar({
    products,
    key = Date.now().toString(),
  }: {
    products: Product[];
    key: string;
  }) {
    this.root.render(
      <SimilarDrawer list={products} key={key} loading={false} />,
    );
  }

  renderSimilarLoader({ key }: { key: string }) {
    this.root.render(<SimilarDrawer list={[]} loading={true} key={key} />);
  }

  renderLowTicketRecommendation(
    props: {
      key: string;
      loading: boolean;
      currentProductHandle: string;
      s: any;
      c: any;
      lowTicketSize: any;
    },
    element: HTMLElement,
  ) {
    const { key, loading, currentProductHandle, s, c, lowTicketSize } = props;

    const child = document.createElement("div");
    element.appendChild(child);
    const root = createRoot(child);
    window.crossSellVariable = crossSellVariable;
    const showAddToCartButton = window.location.pathname.includes("/track")
      ? false
      : true;
    root.render(
      <>
        <div>
          <div className="hp-flex hp-flex-col hp-gap-1 hp-mt-2 hp-rounded-xl hp-border hp-shadow hp-max-h-[450px] hp-overflow-hidden bg-white text-card-foreground">
            <div className="hp-bg-white hp-rounded-sm hp-overflow-scroll">
              <Recommendation
                type={"low-ticket-size"}
                similarLst={s}
                complementartLst={c}
                key={key}
                showComplementaryGrid={false}
                loading={loading}
                hideTypeSelectionTab={true}
                showAddToCartButton={showAddToCartButton}
                currentProductHandle={currentProductHandle}
                defaultSelected
                lowTicketSize={lowTicketSize}
                showInCart={true}
              />
            </div>
          </div>
        </div>
      </>,
    );
  }

  renderComplementaryStylesRecommendation(
    props: {
      key: string;
      loading: boolean;
      productHandles: Record<string, string>[];
      s: any;
      c: any;
      complementaryStyles: Record<string, any>;
      productPositions: Record<string, any>;
    },
    element: HTMLElement,
  ) {
    const {
      key,
      loading,
      productHandles,
      s,
      c,
      productPositions,
      complementaryStyles,
    } = props;

    const child = document.createElement("div");
    element.appendChild(child);
    const root = createRoot(child);

    // window.crossSellVariable = crossSellVariable;
    root.render(
      <div className="">
        <div className="hp-flex hp-flex-col hp-gap-1 hp-mt-2 hp-rounded-xl hp-border hp-shadow bg-white text-card-foreground">
          <div className="hp-p-4 md:hp-p-6">
            <h5 className="md:hp-text-lg hp-text-base hp-flex hp-gap-2 hp-mb-4 hp-font-semibold">
              <Shirt className="hp-w-5 hp-w-5" />
              {"Pieces That Just Click"}
            </h5>

            <div className="hp-bg-white hp-rounded-sm">
              <ComplementaryStylesRecommendation
                handles={productHandles}
                key={key}
                loading={loading}
                similarLst={s}
                complementartLst={c}
                complementaryStyles={complementaryStyles}
                productPositions={productPositions}
              />
            </div>
          </div>
        </div>
      </div>,
    );
  }

  renderButtonDrawer({
    c,
    s,
    key = Date.now().toString(),
    type,
    currentProductHandle,
    loading = true,
    complementaryStyles,
    currentProductPosition,
    lowTicketSize,
  }: {
    c: any;
    s: any;
    key?: string;
    type?: "recommended" | "complete-the-look";
    currentProductHandle?: string;
    loading?: boolean;
    currentProductPosition?: string;
    complementaryStyles?: Record<
      string,
      {
        position: string;
        results: string[];
        subcategory: string;
      }
    >;
    lowTicketSize?: any;
  }) {
    this.root.render(
      <HeliumDrawer key={key}>
        <Recommendation
          type={type}
          similarLst={s}
          complementartLst={c}
          key={key}
          loading={loading}
          currentProductHandle={currentProductHandle}
          complementaryStyles={complementaryStyles}
          currentProductPosition={currentProductPosition}
          lowTicketSize={lowTicketSize}
        />
      </HeliumDrawer>,
    );
  }

  renderComplementaryLoader({ key }: { key: string }) {
    this.root.render(<SimilarDrawer list={[]} loading={true} key={key} />);
  }

  renderFaq(element: HTMLElement, faqs: FAQ[]) {
    const child = document.createElement("div");
    element.appendChild(child);
    const root = createRoot(child);
    root.render(<FaqPillet faqs={faqs} />);
  }

  renderSimilarInlineBlock(element: HTMLElement, handle?: string) {
    const newChild = document.createElement("div");
    element.appendChild(newChild);
    const root = createRoot(newChild);

    root.render(<InlineBlock type="similar" />);
  }

  async renderCartInlineBlock({
    element,
    similarRecc,
    similarTitle,
    complementartyRecc,
    complementartyTitle,
  }: {
    element: HTMLElement;
    similarRecc: Product[];
    complementartyRecc: Product[];
    similarTitle?: string;
    complementartyTitle?: string;
  }) {
    const root = createRoot(element);
    root.render(
      <CartBlock
        {...{
          similarRecc,
          similarTitle,
          complementartyRecc,
          complementartyTitle,
        }}
      />,
    );
  }

  async renderInlineBlock(
    element: HTMLElement,
    products: Product[],
    title?: string,
    variant?: "default" | "secondary",
    type?: ComponentTypes,
    evData: Record<string, any> = {},
    settings?: {
      showCount?: boolean;
      productCount?: number;
      rootStyle?: string;
      addToCart?: boolean;
    },
  ) {
    try {
      const existingProductList = element.querySelector(
        '[data-helium-name="product-list"]',
      );

      if (existingProductList) {
        console.warn("Existing product list found. Removing it.");
        existingProductList.remove();
      }

      if (element.firstChild) {
        console.warn("Existing content found. Clearing and re-rendering.");
        element.innerHTML = "";
        existingProductList?.remove();
      }

      // Apply custom root style if provided
      if (settings?.rootStyle) {
        element.setAttribute("style", settings.rootStyle);
      }

      if (title) {
        let header = title;
        const titleElement = document.createElement("h3");
        const titleSpan = document.createElement("span");
        titleSpan.setAttribute("style", "flex:1;");

        titleElement.classList.add(`hp-heading`);
        titleElement.setAttribute(
          "style",
          "font-size: 20px;margin-bottom:12px; font-weight: 600;display:flex;flex-direction:row;gap:2px;align-items:center;",
        );
        titleElement.appendChild(titleSpan);
        titleSpan.textContent = header;
        if (settings?.showCount) {
          const countElement = document.createElement("span");
          countElement.classList.add("hp-text-sm");
          countElement.setAttribute(
            "style",
            "fontWeight:normal;textAlign:end;color:#666;width:fit-content;",
          );

          countElement.textContent = `${settings.productCount} product${settings.productCount !== 1 ? "s" : ""}`;
          titleElement.appendChild(countElement);
        }
        element.appendChild(titleElement);
      }

      let productListElement = element.querySelector(
        '[data-helium-name="product-list"]',
      ) as HTMLElement;

      console.info("Creating product list element", element);

      if (!productListElement) {
        productListElement = document.createElement("div");
        productListElement.setAttribute("data-helium-name", "product-list");
        productListElement.className = `w-full`;
        element.appendChild(productListElement);
      }
      addViewListener(productListElement, {
        componentType: type ?? "tag_recommendations",
        eventData: evData,
      });

      const productList = document.createElement("ul");

      productList.className = `swimlane2 no-scrollbar flex w-full hp-gap-[var(--h-gap,16px)] hp-pb-2 hp-pl-0 place-items-center ${variant === "secondary" && "pl-0 pb-6"} ${window.helium.productListClasses}`;

      const existingNode = createElementFromString(window.helium.productCard);
      if (!existingNode) {
        console.error("No valid product card template found.");
        return;
      }

      const existingNodeHandle = existingNode.getAttribute(
        "data-helium-product-handle",
      );

      const existingP = (
        await window.helium.datastore.getProduct({
          id: existingNodeHandle ?? "",
        })
      ).details;
      products.forEach((product) => {
        const newNode = cloneNode(
          existingNode,
          product,
          existingP,
          false,
          {
            componentType: type,
            eventData: {
              ...evData,
            },
          },
          {
            addToCart: settings?.addToCart,
          },
        );
        newNode.className +=
          " !hp-w-[var(--h-card-width,200px)] !hp-min-w-[var(--h-card-width,200px)] !hp-max-w-[var(--h-card-width,200px)]";

        if (type) {
          newNode.addEventListener("click", (e) => {
            window.trackHeliumEvent({
              name: "product-clicked",
              type: type,
              properties: {
                current: window.location.pathname.split("/").at(-1),
                cardProduct: product.handle,
              },
            });
          });
        }

        productList.appendChild(newNode);
      });

      productListElement.appendChild(productList);

      if (products.length === 0) {
        const emptyMessage = document.createElement("div");
        emptyMessage.className = "px-4 text-center";
        emptyMessage.textContent = `No ${element.getAttribute("data-helium-cart-inline-block-type") || ""} products found`;
        productListElement.appendChild(emptyMessage);
      }

      try {
        if (element) {
          const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
              handleEvent({
                name: "tag-recommendation",
                properties: { category: "" },
              });
            }
          }, options);
          observer.observe(element);
        }
      } catch (e) {
        console.log("Error rendering inline block", e);
      }
      if (!element.contains(productListElement)) {
        element.appendChild(productListElement);
      }
      console.log("Rendering inline block successful");
    } catch (e) {
      console.log("Error rendering inline block", e);
    }
  }

  renderDynamicText(texts: string[]) {
    return "hello";
  }
  renderRecentlyViewed(element: HTMLElement, products: Product[]) {
    console.log("recently viewed called");
    const newChild = document.createElement("div");
    const child = element.firstChild;
    if (child) {
      return;
    }
    element.appendChild(newChild);
    const root = createRoot(newChild);

    root.render(<RecentlyViewed products={products} />);
  }

  getBannerData(images: string[]) {
    return [
      {
        imgSrc:
          "https://cdn.shopify.com/s/files/1/0569/2132/5643/files/7883dc186e15bf29dad696e1e989e914.jpg?v=1717079102",
        imgAlt: "Helium Pulse",
        h2Text: "Helium Pulse",
        btnText: "Shop Now",
      },
    ];
  }

  renderFilters(element: HTMLElement) {
    const existingFilterContainer = document.getElementById("helium-filters");
    if (existingFilterContainer) {
      console.log("Filters already exist, not rendering again");
      return;
    }
    element.id = "helium-filters";
    const root = createRoot(element);
    root.render(<Filters />);
    console.log("Filters rendered", root);
  }
  renderCartGoals({
    element,
    type,
    data,
  }: {
    element: HTMLElement;
    type: "order-value" | "product-count";
    data: any;
  }) {
    element.className = "my-2";
    const root = createRoot(element);
    root.render(<CartGoals type={type} data={data} />);
  }
  renderMemoryRecommentaion(
    element: HTMLElement,
    subcategory: string,
    memoryData: ActiveMemory,
  ) {
    const child = document.createElement("div");
    element.appendChild(child);
    const root = createRoot(child);
    root.render(
      <MemoryRecomendationPillets
        subcategory={subcategory}
        memoryData={memoryData}
      />,
    );
  }
  renderFloatingChat(element: HTMLElement) {
    const child = document.createElement("div");
    element.appendChild(child);
    const root = createRoot(child);
    root.render(<FloatingChat />);
  }

  renderRecommendationWithoutDrawer({
    c,
    s,
    key = Date.now().toString(),
    type,
    currentProductHandle,
    loading = true,
    complementaryStyles,
    currentProductPosition,
    lowTicketSize,
    element,
    sdk,
  }: {
    c: any;
    s: any;
    key?: string;
    currentProductHandle?: string;
    loading?: boolean;
    currentProductPosition?: string;
    complementaryStyles?: Record<
      string,
      {
        position: string;
        results: string[];
        subcategory: string;
      }
    >;
    lowTicketSize?: any;
    element: HTMLElement;
    sdk: UISDK;
  }) {
    const child = document.createElement("div");
    element.appendChild(child);
    const root = createRoot(child);
    root.render(
      <RecommendationWithoutDrawer
        type={type}
        similarLst={s}
        complementartLst={c}
        key={key}
        loading={loading}
        currentProductHandle={currentProductHandle}
        complementaryStyles={complementaryStyles}
        currentProductPosition={currentProductPosition}
        lowTicketSize={lowTicketSize}
        showComplementaryGrid={true}
        sdk={sdk}
      />,
    );
  }
  renderFestivalCollection(
    element: HTMLElement,
    collections: {
      collectionName: string;
      products: string[];
    }[],
    activeHoliday: {
      name: string;
      description: string;
      eventStart: string;
      eventEnd: string;
    },
  ) {
    const child = document.createElement("div");
    element.appendChild(child);
    const root = createRoot(child);
    root.render(
      <FestivalCollection
        festivalCollections={collections}
        activeHoliday={activeHoliday}
      />,
    );
  }
  renderCollectionFilters(
    element: HTMLElement,
    filterData: Record<string, string[] | string>,
  ) {
    const child = document.createElement("div");
    element.appendChild(child);
    const root = createRoot(child);
    root.render(<CollectionFilters filterData={filterData} />);
  }
  renderPriceLevelCollections(element: HTMLElement) {
    const child = document.createElement("div");
    element.appendChild(child);
    const root = createRoot(child);
    root.render(<PriceLevelColllection />);
  }
}
